import { Link } from 'react-router-dom';
import { ArrowRight, BriefcaseBusiness, Camera, CheckCircle2, Gift, HeartHandshake, Images, Palette, Sparkles } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const souvenirIdeas = [
  {
    icon: Images,
    title: 'Un souvenir d’équipe',
    photo: 'Une photo de groupe nette, un moment de chantier, de bureau ou d’atelier que la personne reconnaîtra immédiatement.',
    style: 'Aquarelle pour une ambiance chaleureuse ou croquis pour un rendu sobre qui s’intègre facilement.',
    source: 'team_memory',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Un clin d’œil au métier',
    photo: 'Un lieu emblématique, un outil, un véhicule ou une scène qui raconte son parcours sans dépendre d’un texte trop long.',
    style: 'Peinture à l’huile pour un souvenir marquant ou pop art pour un départ plus joyeux et décalé.',
    source: 'career_memory',
  },
  {
    icon: HeartHandshake,
    title: 'La nouvelle aventure',
    photo: 'Un voyage, une passion, une maison de famille ou un paysage qui évoque ce que la personne aime faire hors du travail.',
    style: 'Impressionnisme pour le décor ou aquarelle pour un tableau lumineux et personnel.',
    source: 'next_chapter',
  },
];

const photoChecklist = [
  'Choisissez un souvenir que la personne identifiera sans avoir besoin d’une longue explication.',
  'Préférez des visages assez grands et nets plutôt qu’une photo d’équipe prise de très loin.',
  'Évitez les captures d’écran, les images très compressées et les détails importants coupés au bord.',
  'Si le cadeau vient d’un groupe, validez d’abord la photo et le style entre organisateurs avant de créer.',
];

const styleSuggestions = [
  { to: '/styles/watercolor', title: 'Aquarelle', helper: 'Pour un souvenir d’équipe, de famille ou de voyage doux et facile à exposer.' },
  { to: '/styles/oil-painting', title: 'Peinture à l’huile', helper: 'Pour marquer une longue carrière avec un portrait ou un lieu au rendu intemporel.' },
  { to: '/styles/sketch', title: 'Croquis', helper: 'Pour un cadeau collectif sobre à partir d’une photo déjà riche en émotion.' },
  { to: '/styles/pop-art', title: 'Pop art', helper: 'Pour un départ convivial, une photo expressive ou un clin d’œil plein d’énergie.' },
];

const planningSteps = [
  { title: '1. Partir d’une histoire commune', text: 'Équipe, métier, lieu ou passion : choisissez le souvenir qui représente le mieux la personne et son parcours.' },
  { title: '2. Garder la photo lisible', text: 'Un groupe resserré ou un sujet principal clair donnera un tableau plus facile à comprendre et à afficher.' },
  { title: '3. Adapter le style à la personne', text: 'Un rendu doux, classique, sobre ou graphique doit correspondre à sa personnalité et à son futur intérieur.' },
];

const relatedPages = [
  { to: '/idees-cadeaux-photo-personnalises', title: 'Toutes les idées cadeaux photo', helper: 'Comparer d’autres occasions et choisir le bon angle à partir d’un souvenir personnel.' },
  { to: '/cadeau-anniversaire-photo-personnalise', title: 'Cadeau anniversaire photo', helper: 'Retrouver des conseils utiles pour choisir une photo selon la personne qui reçoit le cadeau.' },
  { to: '/tableau-photo-personnalise', title: 'Tableau photo personnalisé', helper: 'Comprendre le parcours complet, de la photo et du style jusqu’au support imprimé.' },
];

const createPath = (source: string) => `/create?source=retirement_gift_${source}`;

const trackRetirementCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'cadeau-depart-retraite-photo-personnalise',
    source: `retirement_gift_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/cadeau-depart-retraite-photo-personnalise'),
      name: 'Cadeau de départ à la retraite photo personnalisé',
      description: 'Guide pour créer un cadeau de départ à la retraite personnalisé à partir d’une photo d’équipe, de métier, de voyage ou de famille.',
      url: absoluteUrl('/cadeau-depart-retraite-photo-personnalise'),
      isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: absoluteUrl() },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: absoluteUrl() },
        { '@type': 'ListItem', position: 2, name: 'Cadeau de départ à la retraite photo', item: absoluteUrl('/cadeau-depart-retraite-photo-personnalise') },
      ],
    },
  ],
};

const CadeauDepartRetraitePhotoPersonnalise = () => (
  <>
    <SEOHead
      title="Cadeau départ retraite photo personnalisé | Ma Photo en Tableau"
      description="Créez un cadeau de départ à la retraite personnalisé à partir d’une photo d’équipe, de métier ou de voyage. Conseils photo, styles et création en ligne."
      canonical={absoluteUrl('/cadeau-depart-retraite-photo-personnalise')}
      schema={schema}
    />
    <div className="min-h-screen overflow-x-hidden">
      <main>
        <section className="section-shell gradient-hero relative overflow-hidden pb-20 pt-32">
          <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
          <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
          <div className="container relative mx-auto grid items-center gap-12 px-6 lg:grid-cols-[1fr_0.9fr]">
            <div className="max-w-3xl">
              <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">Cadeau départ retraite photo personnalisé</p>
              <h1 className="display-title mb-6 text-foreground">Transformez une carrière en <span className="text-premium">souvenir à afficher</span>.</h1>
              <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">Photo d’équipe, lieu emblématique, passion ou prochain voyage : partez d’un souvenir qui raconte vraiment la personne, puis choisissez le style artistique adapté à son départ.</p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                  <Link to={createPath('hero')} onClick={() => trackRetirementCta('hero')}>Créer le cadeau retraite <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                  <Link to="/idees-cadeaux-photo-personnalises">Voir toutes les idées cadeaux</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
              <img src="/site-assets/gift-unwrapping.jpg" alt="Tableau photo personnalisé offert pour un départ à la retraite" width={800} height={533} className="mb-6 aspect-[3/2] w-full rounded-[1.5rem] object-cover" />
              <Gift className="mb-5 h-10 w-10 text-primary" />
              <h2 className="font-display text-4xl leading-none text-foreground">Le bon cadeau commence par un souvenir précis, pas par un objet générique.</h2>
              <div className="mt-6 space-y-4">
                {photoChecklist.map((tip) => <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" /><span>{tip}</span></div>)}
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 max-w-3xl"><p className="eyebrow mb-3">Choisir le souvenir</p><h2 className="section-title text-foreground">Quelle photo pour un départ à la retraite ?</h2><p className="mt-4 text-lg leading-8 text-foreground/65">Reliez la photo au parcours professionnel ou au nouveau chapitre qui commence pour rendre le cadeau immédiatement personnel.</p></div>
            <div className="grid gap-5 md:grid-cols-3">
              {souvenirIdeas.map((idea) => (
                <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary"><idea.icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                  <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                  <Button variant="premium" className="mt-6 rounded-full" asChild><Link to={createPath(idea.source)} onClick={() => trackRetirementCta(idea.source)}>Partir de cette idée <ArrowRight className="h-4 w-4" /></Link></Button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto grid gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-8 shadow-card backdrop-blur-md"><Camera className="mb-5 h-8 w-8 text-primary" /><h2 className="section-title text-foreground">Préparer un cadeau collectif sans compliquer le choix.</h2><p className="mt-5 text-sm leading-7 text-foreground/68">Une personne centralise la photo, puis le groupe valide seulement le souvenir et l’ambiance visuelle. Une composition simple évite de transformer le tableau en montage surchargé.</p></div>
            <div className="grid gap-4 md:grid-cols-3">{planningSteps.map((step) => <div key={step.title} className="rounded-[1.5rem] border border-white/65 bg-white/60 p-5 shadow-card"><h3 className="font-display text-2xl leading-none text-foreground">{step.title}</h3><p className="mt-3 text-sm leading-7 text-foreground/62">{step.text}</p></div>)}</div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end"><div><p className="eyebrow mb-3">Styles conseillés</p><h2 className="section-title text-foreground">Quel style pour ce souvenir ?</h2></div><Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild><Link to={createPath('styles')} onClick={() => trackRetirementCta('styles')}>Tester avec ma photo</Link></Button></div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">{styleSuggestions.map((style) => <Link key={style.to} to={style.to} className="group rounded-[1.6rem] border border-white/65 bg-white/60 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85"><Palette className="mb-4 h-6 w-6 text-primary" /><h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{style.title}</h3><p className="mt-3 text-sm leading-7 text-foreground/62">{style.helper}</p><span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">Voir le style <ArrowRight className="h-4 w-4" /></span></Link>)}</div>
          </div>
        </section>

        <section className="section-shell py-20"><div className="container mx-auto grid gap-5 px-6 md:grid-cols-3">{relatedPages.map((page) => <Link key={page.to} to={page.to} className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85"><h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{page.title}</h3><p className="mt-4 text-sm leading-7 text-foreground/65">{page.helper}</p><span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">Continuer le guide <ArrowRight className="h-4 w-4" /></span></Link>)}</div></section>

        <section className="section-shell pb-24 pt-10"><div className="container mx-auto px-6 text-center"><div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10"><Sparkles className="mx-auto mb-5 h-8 w-8 text-primary" /><h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à transformer ce souvenir ?</h2><p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">Importez la photo choisie par le groupe et explorez le style qui correspond le mieux à la personne et à son histoire.</p><Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild><Link to={createPath('final')} onClick={() => trackRetirementCta('final')}>Commencer la création <ArrowRight className="h-4 w-4" /></Link></Button></div></div></section>
      </main>
      <Footer />
    </div>
  </>
);

export default CadeauDepartRetraitePhotoPersonnalise;
